import React, { useEffect, useState } from 'react';
import { Package, ArrowLeft, Truck, CheckCircle, Clock, XCircle, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getBuyerOrders } from '../services/orderService';
import { Order } from '../types';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'delivered'>('all');

  useEffect(() => {
    if (user) {
      loadOrders();
    }
  }, [user]);

  const loadOrders = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getBuyerOrders(user.id);
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="text-yellow-500" size={20} />;
      case 'accepted':
        return <CheckCircle className="text-blue-500" size={20} />;
      case 'shipped':
        return <Truck className="text-purple-500" size={20} />;
      case 'delivered':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'rejected':
        return <XCircle className="text-red-500" size={20} />;
      default:
        return <Clock className="text-gray-500" size={20} />;
    }
  };

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(order => order.status === filter);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 pb-24">
      {/* Header */}
      <div className="bg-white shadow-md p-4 flex items-center gap-4 sticky top-0 z-10">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/profile')}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft size={24} />
        </motion.button>
        <h1 className="text-xl font-bold text-gray-800">My Orders</h1>
      </div>

      <div className="p-4">
        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide">
          {['all', 'pending', 'accepted', 'delivered'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status as any)}
              className={`px-4 py-2 rounded-xl whitespace-nowrap font-medium transition-all ${
                filter === status
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {filteredOrders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="w-32 h-32 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package size={64} className="text-primary" />
            </div>
            <p className="text-gray-600 mb-4 text-lg">No orders found</p>
            <button
              onClick={() => navigate('/explore')}
              className="px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-indigo-900 transition-colors"
            >
              Start Shopping
            </button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order, index) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
              >
                {/* Order Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Order ID</p>
                    <p className="font-mono text-sm font-semibold text-gray-800">
                      #{order.id.slice(0, 8)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(order.status)}
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(order.status)}`}>
                      {order.status.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Artwork Info */}
                <div className="flex gap-4 mb-4">
                  <img
                    src={order.artworkImage}
                    alt={order.artworkTitle}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-1">{order.artworkTitle}</h3>
                    <p className="text-sm text-gray-600 mb-1">by {order.sellerName}</p>
                    <p className="text-sm text-gray-500">Qty: {order.quantity}</p>
                  </div>
                </div>

                {/* Price & Date */}
                <div className="border-t border-gray-200 pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Amount</span>
                    <span className="font-bold text-primary text-lg">
                      ₹{order.totalAmount.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Order Date</span>
                    <span className="text-gray-800">
                      {format(order.createdAt, 'MMM dd, yyyy')}
                    </span>
                  </div>

                  {order.expectedDeliveryDate && (
                    <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3 mt-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="text-blue-600" size={18} />
                        <span className="text-sm font-medium text-blue-800">Expected Delivery</span>
                      </div>
                      <span className="text-sm font-bold text-blue-900">
                        {format(order.expectedDeliveryDate, 'MMM dd, yyyy')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Shipping Address */}
                <div className="mt-4 bg-gray-50 rounded-lg p-3">
                  <p className="text-sm font-semibold text-gray-800 mb-1">Shipping Address</p>
                  <p className="text-sm text-gray-600">
                    {order.shippingAddress.name}, {order.shippingAddress.phone}
                  </p>
                  <p className="text-sm text-gray-600">
                    {order.shippingAddress.street}, {order.shippingAddress.city}
                  </p>
                  <p className="text-sm text-gray-600">
                    {order.shippingAddress.state} - {order.shippingAddress.pincode}
                  </p>
                </div>

                {/* Action Button */}
                {order.status === 'shipped' && (
                  <button className="w-full mt-4 px-4 py-3 bg-gradient-to-r from-primary to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all">
                    Track Order
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;